import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { X, Eye, EyeOff } from "lucide-react"
import { cn } from "@vc/lib/utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

interface MeetingPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (password: string) => void
  onCancel: () => void
  /** Why the last attempt failed, from the server. The dialog only knew how to
   *  say "enter a password"; whether the one entered was correct is something
   *  only the API can answer, and the joiner needs to be told which it was. */
  errorMessage?: string
}

export function MeetingPasswordDialog({
  open,
  onOpenChange,
  onSubmit,
  onCancel,
  errorMessage,
}: MeetingPasswordDialogProps) {
  const t = useTranslation();
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState(false)

  const handleSubmit = () => {
    if (!password.trim()) {
      setError(true)
      return
    }
    onSubmit(password.trim())
    setError(false)
  }

  const handleCancel = () => {
    setPassword("")
    setError(false)
    onCancel()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden bg-card">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-stroke-100 dark:border-border">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold text-slate-900 dark:text-foreground/80">
              {t("dialogs.enterMeetingPassword")}
            </DialogTitle>
            <button
              onClick={handleCancel}
              className="rounded-full p-1 text-slate-400 dark:text-muted-foreground hover:text-slate-600 dark:hover:text-foreground hover:bg-slate-100 dark:hover:bg-muted transition-colors"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="px-6 py-6">
          <div className="space-y-2">
            <label
              htmlFor="password"
              className={cn(
                "block text-sm font-medium transition-colors",
                error ? "text-red-500" : "text-slate-700 dark:text-foreground"
              )}
            >
              {t("dialogs.password")}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value.slice(0, 128))
                  setError(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSubmit()
                }}
                autoFocus
                maxLength={128}
                autoComplete="current-password"
                className={cn(
                  "w-full rounded-lg border px-3 py-2.5 pr-10 text-sm text-slate-900 dark:text-foreground/80 placeholder:text-slate-400 dark:placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all",
                  error
                    ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                    : "border-slate-300 focus:border-cyan-500 focus:ring-cyan-200"
                )}
                placeholder={t("dialogs.enterPassword")}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-muted-foreground hover:text-slate-600 dark:hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                <span className="sr-only">
                  {showPassword ? t("dialogs.hidePassword") : t("dialogs.showPassword")}
                </span>
              </button>
            </div>
            {error ? (
              <p className="text-sm text-red-500">
                {t("dialogs.pleaseEnterPassword")}
              </p>
            ) : errorMessage ? (
              <p className="text-sm text-red-500">{errorMessage}</p>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stroke-100 dark:border-border bg-slate-50/50">
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="px-6 border-slate-300 dark:border-border text-slate-700 dark:text-foreground hover:bg-slate-100 dark:hover:bg-muted hover:text-slate-900 dark:hover:text-foreground"
            >
              {t("dialogs.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!password.trim()}
              className="px-8 bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white border-0"
            >
              {t("dialogs.ok")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
